import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import Autocomplete from 'react-autocomplete';
import cn from 'classnames';

import { BRAND_VIEW, RECOMMENDED_SIZE_VIEW } from 'constants/appConstants';
import { HYDRA_SAVE_ODSP } from 'constants/hydraTests';

import { getBrands } from 'selectors/onDemandSizing';
import {
  fetchBrandList,
  fetchSizingPredictionOnDemand,
  fetchSupportedBrands,
  fireOnDemandEvent,
  saveOdsp,
  setModalView,
  updateBrand,
  updateProductCategory,
  updateProductSize
} from 'actions/productdetail/onDemandSizing';
import { trackEvent } from 'helpers/analytics';
import { Clear } from 'components/icons';
import { SmallLoader } from 'components/Loader';
import { getAssignmentGroup, triggerAssignment } from 'actions/ab';
import { redirectToAuthenticationFor } from 'actions/redirect';
import css from 'styles/components/productdetail/recommendedSizeFinderModal.scss';

const BRAND_INPUT_PLACEHOLDER = 'Nike, Adidas, Vans, etc.';
const SIZE_RANGE = [4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10, 10.5, 11, 11.5, 12, 12.5, 13, 13.5, 14, 14.5, 15, 15.5, 16];

export class RecommendedSizeFinderModal extends Component {
  state = {
    clearSavedODSP: false,
    savedSizingInfo: false,
    forceClickedContinue: false
  }

  componentDidMount() {
    const { onDemandSizing: { brandList, supportedBrands }, gender, fetchBrandList, fetchSupportedBrands, triggerAssignment } = this.props;
    triggerAssignment(HYDRA_SAVE_ODSP);

    if (!brandList.length) {
      fetchBrandList();
    }

    if (!supportedBrands.length) {
      fetchSupportedBrands(gender);
    }
  }

  componentDidUpdate(oldProps, oldState) {
    const { predictedSize: oldPredictedSize } = oldProps;
    const { localStorage: { onDemandSize }, onDemandSizing: { brandList, modalView, predictedSize, brandId, productSize, productCategory }, hydraSop, handleCalculateSizeClick, saveOdsp, predictedSize: sizePrediction } = this.props;
    const { clearSavedODSP, savedSizingInfo, forceClickedContinue } = this.state;
    const { showModal, autoCompleteValue, productCategory: localCategory, productSize: localSize } = onDemandSize;
    const canGetData = showModal && brandList.length;
    const hasOnDemandSizingData = brandId && productSize && productCategory;

    if (canGetData && hydraSop && this.customerIsAuthed() && !sizePrediction) {

      if (!savedSizingInfo) {
        this.handleBrandSelect(autoCompleteValue);
        this.handleProductCategoryChange(null, localCategory);
        this.handleProductSizeChange(null, localSize);
        this.setState({ savedSizingInfo: true });
      }

      if (hasOnDemandSizingData) {
        if (!forceClickedContinue) {
          this.handleContinueClick();
          this.setState({ forceClickedContinue: true });
        }

        if (hydraSop === 1 && predictedSize) {
          handleCalculateSizeClick();
          this.setState({ clearSavedODSP: true });
        }
      }

      if (hydraSop === 2 && forceClickedContinue && modalView === RECOMMENDED_SIZE_VIEW && predictedSize !== oldPredictedSize) {
        this.handleContinueClick();
        this.setState({ clearSavedODSP: true });
      }

      if (clearSavedODSP && clearSavedODSP !== oldState.clearSavedODSP) {
        saveOdsp({});
      }
    }
  }

  customerIsAuthed = () => !!this.props.cookies['x-main'];

  handleClose = () => {
    const { onClose, fireOnDemandEvent } = this.props;

    onClose();
    fireOnDemandEvent('exit');
  }

  handleBackClick = () => {
    const { fireOnDemandEvent, onDemandSizing: { modalView }, setModalView } = this.props;
    if (modalView === RECOMMENDED_SIZE_VIEW) {
      setModalView(BRAND_VIEW);
      fireOnDemandEvent('edit');
    }
  };

  handleBrandChange = (event, value) => {
    const { brands, updateBrand } = this.props;
    const filteredBrands = brands.filter(({ name }) => name.toLowerCase().includes(value.toLowerCase()));
    updateBrand({ brandId: '' , brandName: '', autoCompleteValue: value, isBrandInvalid: !!filteredBrands.length });
  };

  handleBrandSelect = brandName => {
    const { fireOnDemandEvent, onDemandSizing: { brandList }, updateBrand } = this.props;
    const brandId = brandList.find(brand => brand.name === brandName).id;
    updateBrand({ brandId, brandName, autoCompleteValue: brandName });
    fireOnDemandEvent('brand', brandName);
  };

  handleContinueClick = () => {
    const { onDemandSizing, gender, productId, fetchSizingPredictionOnDemand, fireOnDemandEvent, setModalView, onSetRecommendedFit } = this.props;
    const { modalView, predictedSize, brandId, productSize, productCategory } = onDemandSizing;
    if (modalView === BRAND_VIEW) {
      fetchSizingPredictionOnDemand({ productId, brandId, productCategory, gender, productSize });
      setModalView(RECOMMENDED_SIZE_VIEW);
      fireOnDemandEvent('next');
      trackEvent('TE_PDP_ONDEMAND_SIZING', `${productId}:Step:${BRAND_VIEW}`);
    } else if (modalView === RECOMMENDED_SIZE_VIEW && predictedSize) {
      onSetRecommendedFit(predictedSize);
      fireOnDemandEvent('apply');
      trackEvent('TE_PDP_ONDEMAND_SIZING', `${productId}:Step:${RECOMMENDED_SIZE_VIEW}`);
    }
  };

  handleProductCategoryChange = (event, overrideValue = '') => {
    const { updateProductCategory, fireOnDemandEvent } = this.props;
    const categoryValue = event?.target ? event.target.value : overrideValue;
    updateProductCategory(categoryValue);
    fireOnDemandEvent('category', categoryValue);
  };

  handleProductSizeChange = (event, overrideValue = '') => {
    const { updateProductSize, fireOnDemandEvent } = this.props;
    const productSize = event?.target ? event.target.value : overrideValue;
    updateProductSize(productSize);
    fireOnDemandEvent('size', productSize);
  };

  handleSubmit = event => {
    event.preventDefault();
    const { productId, gender, setModalView, fetchSizingPredictionOnDemand, onDemandSizing: { brandId, productCategory, productSize, brandName, isBrandInvalid } } = this.props;
    if (brandName && !isBrandInvalid) {
      setModalView(RECOMMENDED_SIZE_VIEW);
      trackEvent('TE_PDP_ONDEMAND_SIZING', `${productId}:Step:${BRAND_VIEW}`);
    }

    if (productCategory && productSize) {
      fetchSizingPredictionOnDemand({ productId, brandId, productCategory, gender, productSize });
      setModalView(RECOMMENDED_SIZE_VIEW);
      trackEvent('TE_PDP_ONDEMAND_SIZING', `${productId}:Step:${RECOMMENDED_SIZE_VIEW}`);
    }
  };

  makeBrandAndProductCategoryAndSizeView = () => {
    const { brands, onDemandSizing: { autoCompleteValue, isBrandInvalid, hasBrandListLoaded, hasSupportedBrandsLoaded, supportedBrands, brandId, productCategory, productSize } } = this.props;
    const brand = supportedBrands.find(brand => brand.brandId === parseInt(brandId, 10));
    const items = brands.sort((a, b) => {
      const brandA = a.name.toLowerCase();
      const brandB = b.name.toLowerCase();
      return brandA < brandB ? -1 : brandA > brandB ? 1 : 0;
    });
    const isBrandNameValid = items.some(item => item.name === autoCompleteValue);

    const wrapperProps = {
      className: css.textFieldWrapper
    };

    const inputProps = {
      'id': 'brandAutoComplete',
      'className': cn(css.textField, { [css.inputError]: !!autoCompleteValue && !isBrandNameValid }),
      'placeholder': (hasBrandListLoaded && hasSupportedBrandsLoaded) ? BRAND_INPUT_PLACEHOLDER : 'Loading...',
      'disabled': !hasBrandListLoaded || !hasSupportedBrandsLoaded
    };

    return (
      <div className={css.mainContent}>
        <h1>Let's Find Your Size</h1>
        <form method="get" onSubmit={this.handleSubmit}>
          <div className={css.brandAutoComplete}>
            <label htmlFor="brandAutoComplete">What brand of shoe fits you well?</label>
            <Autocomplete
              wrapperProps={wrapperProps}
              inputProps={inputProps}
              items={items}
              getItemValue={item => item.name}
              shouldItemRender={this.matchBrandToTerm}
              renderMenu={(items, value, style) => {
                const menuStyles = { ...style, left: 0, top: 0, minWidth: style.minWidth ? style.minWidth - 4 : null };
                const menuClasses = cn(css.autoCompleteMenu, { [css.hidden]: !items.length });
                return <div style={menuStyles} className={menuClasses} children={items}/>;
              }}
              renderItem={(item, isHighlighted) => (
                <div key={item.id} className={cn(css.menuItem, { [css.highlighted]: isHighlighted })}>
                  {item.name}
                </div>
              )}
              value={autoCompleteValue}
              onChange={this.handleBrandChange}
              onSelect={this.handleBrandSelect}
              selectOnBlur={true}/>
            {!isBrandNameValid && !isBrandInvalid && !!autoCompleteValue && <p className={css.autoCompleteErrorMessage}>Sorry, please select a valid brand name.</p>}
          </div>
          <div className={css.selectField}>
            <label htmlFor="productCategory">Select the style</label>
            <select
              id="productCategory"
              value={productCategory}
              onChange={this.handleProductCategoryChange}
              disabled={autoCompleteValue === ''}
              required
            >
              <option value="">Select the type of this shoe</option>
              {brand && brand.productCategories.map((category, index) => (
                <option key={index} value={category}>{category}</option>
              ))}
            </select>
          </div>
          <div className={css.selectField}>
            <label htmlFor="productSize">What size?</label>
            <select
              id="productSize"
              value={productSize}
              onChange={this.handleProductSizeChange}
              disabled={productCategory === ''}
              required
            >
              <option value="">Select the size of this shoe</option>
              {SIZE_RANGE.map((size, index) => (
                <option key={index} value={size}>{size}</option>
              ))}
            </select>
          </div>
        </form>
      </div>
    );
  };

  saveOdspClick = () => {
    const { location, onDemandSizing: { autoCompleteValue, productCategory, productSize }, saveOdsp, productId, hydraSop, fireOnDemandEvent, redirectToAuthenticationFor } = this.props;
    const payload = { autoCompleteValue, productCategory, productSize, showModal: true, location: location.pathname };
    const eventData = `${hydraSop}:${productId}`;
    saveOdsp(payload);
    fireOnDemandEvent('save', eventData);
    trackEvent('TE_PDP_ONDEMAND_SIZING_SAVE', eventData);
    redirectToAuthenticationFor(location);
  }

  makeModalFooter = () => {
    const { hydraSop, onDemandSizing: { modalView, brandName, productCategory, productSize, predictedSize, isBrandInvalid, hasBrandListLoaded, hasSupportedBrandsLoaded } } = this.props;
    const isAtEnd = modalView === RECOMMENDED_SIZE_VIEW;
    const isContinueButtonDisabled = !hasBrandListLoaded || !hasSupportedBrandsLoaded ||
      (modalView === BRAND_VIEW && (!brandName || isBrandInvalid || !productCategory || !productSize) ||
      (isAtEnd && !predictedSize));
    const hasSopButton = !this.customerIsAuthed() && ((hydraSop === 1 && !isAtEnd) || (hydraSop === 2 && isAtEnd));
    return (
      <div className={cn(css.modalFooter, { [css.sop]: (hydraSop && hasSopButton) }) }>
        {isAtEnd && (
          <button type="button" className={css.backBtn} onClick={this.handleBackClick}>
            Edit
          </button>
        )}
        <button
          type="button"
          className={css.continueBtn}
          onClick={this.handleContinueClick}
          disabled={isContinueButtonDisabled}
        >
          {isAtEnd ? 'Apply Size' : 'Continue'}
        </button>
        {hasSopButton && <button
          type="button"
          className={css.saveSize}
          onClick={this.saveOdspClick}
          disabled={isContinueButtonDisabled}>Save Size</button>}
      </div>
    );
  };

  makeRecommendedSizeView = () => {
    const { onDemandSizing: { predictedSize, hasPredictionLoaded, uncertainty, brandName, productCategory, productSize }, gender } = this.props;
    if (!hasPredictionLoaded) {
      return (
        <div className={css.mainContent}>
          <SmallLoader/>
        </div>
      );
    } else if (hasPredictionLoaded && !predictedSize) {
      return (
        <div className={css.mainContent}>
          <h1>Error</h1>
          <p>Sorry, we were unable to get a size prediction.</p>
        </div>
      );
    } else {
      return (
        <div>
          <div className={css.mainContent}>
            <h1>Your Recommended Size</h1>
            <p>
              {!uncertainty && `Based on the ${brandName} ${productCategory} size ${productSize} that you wear, we recommend this size:`}
              {uncertainty && `While there is no exact match based on your preference, we recommend rounding the size ${uncertainty >= 0 ? 'up' : 'down'} for this shoe.`}
            </p>
            <div className={css.productInfo}>{gender}'s size {predictedSize}</div>
          </div>
        </div>
      );
    }
  };

  matchBrandToTerm = (brand, value) => brand.name.toLowerCase().indexOf(value.toLowerCase()) !== -1;

  render() {
    const { onDemandSizing: { modalView }, isOpen } = this.props;

    return (
      <Modal
        isOpen={isOpen}
        onRequestClose={this.handleClose}
        portalClassName={css.modalPortal}
        overlayClassName={css.modalOverlay}
        className={css.modalContent}
        contentLabel="RecommendedSizeFinderModal">
        <div className={css.closeLabel}>Close</div>
        <Clear size={24} className={css.close} onClick={this.handleClose}/>
        {modalView === BRAND_VIEW && this.makeBrandAndProductCategoryAndSizeView()}
        {modalView === RECOMMENDED_SIZE_VIEW && this.makeRecommendedSizeView()}
        {this.makeModalFooter()}
      </Modal>
    );
  }
}

RecommendedSizeFinderModal.contextTypes = {
  testId: PropTypes.func
};

RecommendedSizeFinderModal.propTypes = {
  productId: PropTypes.string.isRequired,
  gender: PropTypes.string.isRequired,
  onSetRecommendedFit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
};

const mapStateToProps = state => {
  const { cookies, localStorage, product, sizingChooser: { onDemandSizing } } = state;
  return {
    brands: getBrands(onDemandSizing),
    cookies,
    localStorage,
    product,
    onDemandSizing,
    hydraSop: getAssignmentGroup(HYDRA_SAVE_ODSP, state)
  };
};

export default connect(mapStateToProps, {
  fetchBrandList,
  fetchSupportedBrands,
  fetchSizingPredictionOnDemand,
  fireOnDemandEvent,
  redirectToAuthenticationFor,
  saveOdsp,
  setModalView,
  triggerAssignment,
  updateProductCategory,
  updateProductSize,
  updateBrand
})(RecommendedSizeFinderModal);
